import { DataDefinition, DataType } from "@soft-db/data-formatter";
import { type FieldFactory } from "@soft-db/form";
import { Show } from "@soft-db/soft-utils";
import type { LiteralToPrimitiveDeep, UnionToIntersection } from "type-fest";
import { PropType } from "vue";
import { FieldRule } from "../../../../../packages/form/src/field-rule";
import QueryDataConfig, { QueryDataConfigForm } from "./QueryDataConfig";
import QueryTypeConfig, { QueryTypeConfigForm } from "./QueryTypeConfig";

export type QueryConfigForm = QueryTypeConfigForm & QueryDataConfigForm;

const temporarySubType: DataType[] = ["weather", "dust"];

export function parseQueryConfigForm(form: Partial<QueryConfigForm>): DataDefinition {
	const definition = { ...form.$dataConfig, ...form.$typeConfig } as any;
	if (temporarySubType.includes(definition.type)) {
		delete definition.subType;
	}

	return definition as DataDefinition;
}

export function createQueryConfigForm(definition: DataDefinition): QueryConfigForm {
	const {
		type,
		subType: _subType,
		unit,
		...rest
	} = definition as UnionToIntersection<LiteralToPrimitiveDeep<DataDefinition>>;

	let subType = _subType;
	if (temporarySubType.includes(type as any)) {
		subType = rest.data;
	}

	return {
		$typeConfig: { type, subType, unit } as any,
		$dataConfig: { ...rest } as any,
	};
}

export default defineComponent({
	name: "QueryConfig",
	props: {
		useField: {
			type: Function as PropType<FieldFactory<QueryConfigForm>>,
			required: true,
		},
		readonly: Boolean,
		showPeriod: Boolean,
		allowIntervals: Boolean,
		showSpectrums: Boolean,
		periodRules: Object as PropType<FieldRule<QueryDataConfigForm>>,
		hideTypeConfig: Boolean,
		hideDataCondig: Boolean,
	},
	setup(props) {
		const type = props.useField("$typeConfig.type");
		const subType = props.useField("$typeConfig.subType");

		const dataConfig = props.useField("$dataConfig");

		function clearDataConfig() {
			dataConfig.clear();
		}

		watch(type.data, (type, prevType) => {
			if (prevType == null) return;

			if (type !== prevType) {
				clearDataConfig();
			}
		});

		watch(subType.data, (subType, prevSubType) => {
			if (prevSubType == null) return;

			if (subType !== prevSubType) {
				clearDataConfig();
			}
		});

		return () => (
			<>
				<Show when={!props.hideTypeConfig}>
					<QueryTypeConfig useField={props.useField} readonly={props.readonly} />
				</Show>

				<Show when={!props.hideDataCondig}>
					<QueryDataConfig
						type={type.data.value}
						subType={subType.data.value}
						showPeriod={props.showPeriod}
						allowIntervals={props.allowIntervals}
						periodRules={props.periodRules}
						showSpectrum={props.showSpectrums}
						useField={props.useField}
					/>
				</Show>
			</>
		);
	},
});
